// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * Where the camera is along the hole through the video: distance to the green in metres,
 * sampled every `intervalMs`. Written by the render job from the flight it actually flew —
 * the client interpolates between these rather than working the flight out for itself,
 * which it cannot: the camera is cosine-eased along a curve bent through the fairway.
 */
export type FlyoverDistances = {
  /** Sample `i` is `i / (length - 1)` of the way through the video. */
  toGreenM: number[];
  /** The hole along the line flown, so a distance from the tee is this less `toGreenM`. */
  holeLengthM: number;
};

/** One rendered flyover: the 16:9 video, and the 3:5 phone render where it has been made. */
export type FlyoverVideo = {
  url: string;
  portrait: { url: string; width: number; height: number } | null;
  /** Null on a course rendered before the track existed; the UI then shows no distance. */
  distances: FlyoverDistances | null;
};

/**
 * Pre-rendered flyover videos per hole, when the offline render script has produced them
 * for this course. An empty map is the ordinary answer — most courses have none — and any
 * failure reads the same way: the 2D flyover simply plays instead.
 */
export const useFlyoverVideos = (
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  enabled: boolean
): Record<number, FlyoverVideo> => {
  const [byHole, setByHole] = useState<Record<number, FlyoverVideo>>({});

  useEffect(() => {
    if (!enabled || !token || !courseId) {
      setByHole({});
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/flyovers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok || cancelled) return;
        const payload = (await response.json()) as {
          holes?: {
            holeNumber?: number;
            url?: string;
            portrait?: { url?: string; width?: number; height?: number } | null;
            distances?: { toGreenM?: number[]; holeLengthM?: number } | null;
          }[];
        };
        if (cancelled) return;
        const next: Record<number, FlyoverVideo> = {};
        for (const hole of payload.holes ?? []) {
          if (!hole.holeNumber || !hole.url) continue;
          const portrait = hole.portrait;
          const distances = hole.distances;
          next[hole.holeNumber] = {
            url: hole.url,
            portrait:
              portrait?.url && portrait.width && portrait.height
                ? { url: portrait.url, width: portrait.width, height: portrait.height }
                : null,
            distances:
              distances?.holeLengthM && (distances.toGreenM?.length ?? 0) > 1
                ? {
                    toGreenM: distances.toGreenM as number[],
                    holeLengthM: distances.holeLengthM,
                  }
                : null,
          };
        }
        setByHole(next);
      } catch {
        // Decoration only; the 2D flyover needs nothing from here.
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseId, enabled, token]);

  return byHole;
};
